import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Runtime co-branding (WS2). Fetches /api/branding (public, Host-resolved) and
// re-skins the app to the group's colors by overriding the Tailwind primary CSS
// vars on :root. Crucially it regenerates the WHOLE primary ramp (50→900) from
// the chosen color, so no un-overridden teal shade can leak through (the login
// gradient, light backgrounds, badges all read from these). No-op on the main
// domain (default RefDash skin). Our brand line stays in the shell — co-brand.

export interface Branding {
  tenant: string | null;
  display_name: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
}

const DEFAULT: Branding = {
  tenant: null,
  display_name: 'RefDash',
  logo_url: null,
  logo_dark_url: null,
  primary_color: null,
  accent_color: null,
};

// ── Ramp generation ─────────────────────────────────────────────────────────
type RGB = [number, number, number];

function hexToRgb(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex([r, g, b]: RGB): string {
  return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
}
function mix(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

// A Tailwind-shaped ramp anchored on the brand color (~500/600); lighter stops
// mixed toward white, darker toward near-black — a real designed ramp, not a
// naive gradient.
function buildRamp(base: RGB): Record<string, string> {
  const W: RGB = [255, 255, 255];
  const K: RGB = [17, 17, 20];
  const towardWhite: Record<number, number> = { 50: 0.93, 100: 0.82, 200: 0.64, 300: 0.44, 400: 0.22 };
  const towardBlack: Record<number, number> = { 600: 0.12, 700: 0.26, 800: 0.4, 900: 0.52 };
  const out: Record<string, string> = {
    '--color-primary': rgbToHex(base),
    '--color-primary-500': rgbToHex(base),
  };
  for (const [stop, t] of Object.entries(towardWhite)) out[`--color-primary-${stop}`] = rgbToHex(mix(base, W, t));
  for (const [stop, t] of Object.entries(towardBlack)) out[`--color-primary-${stop}`] = rgbToHex(mix(base, K, t));
  out['--color-accent'] = out['--color-primary-50'];
  return out;
}

// ── Tenant icons ────────────────────────────────────────────────────────────
// The uploaded logo is typically an SVG: fine as a favicon directly, but iOS
// home-screen icons and PWA manifest icons must be PNG — rasterize via canvas.
// Everything is best-effort: on any failure the RefDash icons simply stay.

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

async function rasterize(logoUrl: string, size: number, background?: string): Promise<string | null> {
  try {
    const img = new Image();
    img.src = logoUrl;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (background) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, size, size);
    }
    // Contain-fit the logo with ~12% padding on each side.
    const box = size * 0.76;
    const w = img.naturalWidth || box;
    const h = img.naturalHeight || box;
    const scale = Math.min(box / w, box / h);
    const dw = w * scale;
    const dh = h * scale;
    ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

async function applyTenantIcons(branding: Branding) {
  if (!branding.logo_url) return;
  const isSvg = /\.svg($|\?)/i.test(branding.logo_url) || branding.logo_url.startsWith('/api/');
  // Favicon: SVG (or any image) works as-is.
  setLink('icon', branding.logo_url);
  if (isSvg) {
    const el = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (el) el.type = 'image/svg+xml';
  }
  // iOS home-screen icon: PNG only; iOS fills transparency with black, so
  // paint the tenant's primary (or white) behind the logo.
  const bg = branding.primary_color || '#ffffff';
  const touch = await rasterize(branding.logo_url, 180, bg);
  if (touch) setLink('apple-touch-icon', touch);
  // PWA manifest (Android install): rebuild it under the tenant's name with
  // rasterized icons, served from a blob URL. start_url must stay absolute.
  const [i192, i512] = await Promise.all([
    rasterize(branding.logo_url, 192, bg),
    rasterize(branding.logo_url, 512, bg),
  ]);
  if (!i192 || !i512) return;
  const base = `${window.location.origin}${import.meta.env.BASE_URL || '/'}`;
  const manifest = {
    name: branding.display_name,
    short_name: branding.display_name,
    description: 'Hospital representative approval portal',
    start_url: base,
    scope: base,
    display: 'standalone',
    theme_color: '#FAFAF9',
    background_color: '#FAFAF9',
    icons: [
      { src: i192, sizes: '192x192', type: 'image/png' },
      { src: i512, sizes: '512x512', type: 'image/png' },
    ],
    gcm_sender_id: '103953800507',
  };
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  setLink('manifest', URL.createObjectURL(blob));
}

const BrandingContext = createContext<Branding>(DEFAULT);
export function useBranding(): Branding {
  return useContext(BrandingContext);
}

export function CobrandProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULT);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/branding', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: Branding | null) => {
        if (cancelled || !data) return;
        setBranding({ ...DEFAULT, ...data });
        const root = document.documentElement;
        const base = data.primary_color ? hexToRgb(data.primary_color) : null;
        if (base) {
          for (const [k, v] of Object.entries(buildRamp(base))) root.style.setProperty(k, v);
        }
        if (data.accent_color) root.style.setProperty('--color-accent-foreground', data.accent_color);
        else if (base) root.style.setProperty('--color-accent-foreground', rgbToHex(mix(base, [17, 17, 20], 0.26)));
        if (data.display_name) document.title = data.display_name;
        if (data.tenant) void applyTenantIcons({ ...DEFAULT, ...data });
      })
      .catch(() => { /* best-effort — default skin stays */ });
    return () => { cancelled = true; };
  }, []);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}
